import { useCallback, useSyncExternalStore } from 'react'

const STORAGE_EVENT = 'synced-local-storage-change'

// Read-modify-write against live storage so writes never depend on React
// state. Same-tab subscribers only see writes that go through here.
export const updateSyncedLocalStorage = (
  storageKey: string,
  updater: (raw: string) => string,
) => {
  const raw = window.localStorage.getItem(storageKey) ?? ''
  const next = updater(raw)
  if (next === raw) return
  try {
    window.localStorage.setItem(storageKey, next)
    window.dispatchEvent(new Event(STORAGE_EVENT))
  } catch {
    // Ignore localStorage failures (private browsing, quota, etc.).
  }
}

// Raw string value of a localStorage key ('' when unset or keyless), synced
// across tabs via the native storage event and within the tab via
// STORAGE_EVENT. Re-renders only when the raw string changes.
export const useSyncedLocalStorage = (storageKey: string | undefined) => {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const handleStorage = (event: StorageEvent) => {
        if (event.key === storageKey) onStoreChange()
      }
      window.addEventListener('storage', handleStorage)
      window.addEventListener(STORAGE_EVENT, onStoreChange)
      return () => {
        window.removeEventListener('storage', handleStorage)
        window.removeEventListener(STORAGE_EVENT, onStoreChange)
      }
    },
    [storageKey],
  )
  const getSnapshot = useCallback(
    () => (storageKey ? (window.localStorage.getItem(storageKey) ?? '') : ''),
    [storageKey],
  )
  return useSyncExternalStore(subscribe, getSnapshot, () => '')
}
